import React, { useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "../firebase";
import { styled } from "@mui/material/styles";
import {
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress,
  Grid,
  Fade,
  TextField,
  Divider,
} from "@mui/material";

import { Google, Email } from "@mui/icons-material";
import { withErrorBoundary } from "react-error-boundary";
import GeneralErrorPage from "./GeneralErrorPage";

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: "38px",
  marginBottom: "8px",
  background: "linear-gradient(90deg, #7B1FA2 0%, #4A148C 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  textAlign: "center",
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: "#666",
  fontSize: "16px",
  lineHeight: 1.6,
  marginBottom: "40px",
  textAlign: "center",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0.5),
  padding: theme.spacing(1.5, 3),
  height: "56px",
  width: "100%",
  borderRadius: "16px",
  textTransform: "none",
  fontSize: "15px",
  fontWeight: 500,
  boxShadow: "none",
  border: "1px solid #eee",
  backgroundColor: "#fff",
  color: "#1a1a1a",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "#f8f9fa",
    borderColor: "#7B1FA2",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  },
}));

function SignInScreen() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState({
    message: "",
    open: false,
  });
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError({ message: "", open: false });

    const auth = firebase.auth();

    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          setError({ message: "Passwords don't match", open: true });
          setIsLoading(false);
          return;
        }
        await auth.createUserWithEmailAndPassword(email, password);
      } else {
        await auth.signInWithEmailAndPassword(email, password);
      }
    } catch (error) {
      setIsLoading(false);
      setError({ message: error.message, open: true });
    }
  };

  const handleGoogleLogin = () => {
    setIsLoading(true);
    const auth = firebase.auth();
    const googleProvider = new firebase.auth.GoogleAuthProvider();
    auth
      .signInWithPopup(googleProvider)
      .catch((error) => {
        const fallbackCodes = [
          "auth/popup-closed-by-user",
          "auth/popup-blocked",
          "auth/cancelled-popup-request",
        ];
        if (fallbackCodes.includes(error.code)) {
          // Fallback to redirect flow for browsers that block/close popups
          return auth.signInWithRedirect(googleProvider);
        }
        setIsLoading(false);
        setError({ message: error.message, open: true });
      });
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#fff",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
      }}
    >
      <Grid container>
        {/* Left side - Dark Background with Content */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: "none", md: "flex" },
            background: "linear-gradient(135deg, #7B1FA2 0%, #4A148C 100%)",
            color: "#fff",
            p: 8,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "radial-gradient(circle at 30% 30%, rgba(186, 104, 200, 0.3) 0%, rgba(186, 104, 200, 0.1) 45%, rgba(74, 20, 140, 0.2) 100%)",
              zIndex: 1,
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 2, textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                mb: 3,
                fontWeight: 800,
                fontSize: { md: "48px", lg: "56px" },
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              🏰 Idioma.Quest
            </Typography>
            <Typography
              sx={{
                mb: 3,
                color: "rgba(255,255,255,0.9)",
                fontSize: "20px",
                lineHeight: 1.6,
                maxWidth: "480px",
              }}
            >
              Learn languages through AI-powered interactive adventures. 
              Talk with characters, solve mysteries, and complete quests 
              entirely in your target language.
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.7)",
                fontSize: "16px",
                lineHeight: 1.6,
                maxWidth: "480px",
                fontStyle: "italic",
              }}
            >
              "The best way to learn a language is to use it"
            </Typography>
          </Box>
        </Grid>

        {/* Right side - Login Form */}
        <Fade in={true} timeout={1000}>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              p: { xs: 2, sm: 3, md: 8 },
              maxWidth: { xs: "100%", md: "760px" },
              margin: "0 auto",
            }}
          >
            <Box sx={{ maxWidth: "400px", width: "100%" }}>
              <Title variant="h1">Welcome to Idioma.Quest</Title>
              <Subtitle>
                {isSignUp ? "Create an account" : "Sign in"} to start your language learning adventure
              </Subtitle>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  gap: 2,
                }}
              >
                <StyledButton
                  startIcon={<Google />}
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                >
                  {isLoading ? "Signing in..." : "Continue with Google"}
                </StyledButton>

                <Divider sx={{ my: 2 }}>
                  <Typography sx={{ color: "#999", fontSize: "14px" }}>
                    or
                  </Typography>
                </Divider>

                <Box component="form" onSubmit={handleEmailAuth} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    fullWidth
                    disabled={isLoading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                      },
                    }}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    fullWidth
                    disabled={isLoading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                      },
                    }}
                  />
                  {isSignUp && (
                    <TextField
                      label="Confirm Password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      fullWidth
                      disabled={isLoading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                        },
                      }}
                    />
                  )}
                  <StyledButton
                    type="submit"
                    startIcon={<Email />}
                    disabled={isLoading}
                  >
                    {isLoading ? "Please wait..." : isSignUp ? "Sign Up" : "Sign In"}
                  </StyledButton>
                </Box>

                <Button
                  onClick={() => setIsSignUp(!isSignUp)}
                  disabled={isLoading}
                  sx={{
                    textTransform: "none",
                    color: "#7B1FA2",
                    "&:hover": {
                      backgroundColor: "rgba(123, 31, 162, 0.04)",
                    },
                  }}
                >
                  {isSignUp
                    ? "Already have an account? Sign In"
                    : "Don't have an account? Sign Up"}
                </Button>

                {error.open && (
                  <Alert
                    severity="error"
                    sx={{
                      mt: 2,
                      width: "100%",
                      borderRadius: "16px",
                      border: "1px solid rgba(255, 77, 79, 0.16)",
                      backgroundColor: "rgba(255, 77, 79, 0.04)",
                      "& .MuiAlert-icon": {
                        color: "#ff4d4f",
                      },
                    }}
                  >
                    {error.message}
                  </Alert>
                )}
              </Box>

              <Typography
                sx={{
                  mt: 5,
                  fontSize: "13px",
                  color: "#666",
                  textAlign: "center",
                  lineHeight: 1.6,
                }}
              >
                By continuing, you agree to our Terms of Service and Privacy Policy.
              </Typography>
            </Box>
          </Grid>
        </Fade>
      </Grid>
    </Box>
  );
}

export default withErrorBoundary(SignInScreen, {
  fallback: <GeneralErrorPage />,
});
